import type {
  AppConfig,
  PricingModel,
  WalletTopUpBonusTier,
} from "@nomnom/core";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

export type LocationKind = "diningHalls" | "dorms";

export type LocationItem = {
  id: string;
  name: string;
  active: boolean;
};

export type ConfigFormState = {
  deliveryFee: number;
  pricingModel: PricingModel;
  distanceFeePerMile: number;
  tipPresets: number[];
  walletTopUpPresets: number[];
  walletTopUpBonuses: WalletTopUpBonusTier[];
  pricingVersion: number;
  pricingEffectiveAt: Timestamp | null;
  orderingEnabled: boolean;
  pilotModeEnabled: boolean;
  pilotCohortLabel: string;
  pilotAnnouncement: string;
  launchNightOrderCap: number;
  minimumOnlineRiders: number;
  maxSearchingOrders: number;
  serviceHours: {
    start: string;
    end: string;
  };
  maxSearchRadius: number;
  riderTimeout: number;
};

export const DEFAULT_CONFIG: ConfigFormState = {
  deliveryFee: 2.0,
  pricingModel: "flat",
  distanceFeePerMile: 0.75,
  tipPresets: [1, 2, 3],
  walletTopUpPresets: [5, 10, 20, 50],
  walletTopUpBonuses: [
    { topUpAmount: 20, bonusAmount: 1 },
    { topUpAmount: 50, bonusAmount: 3 },
  ],
  pricingVersion: 1,
  pricingEffectiveAt: null,
  orderingEnabled: true,
  pilotModeEnabled: true,
  pilotCohortLabel: "UCSD dinner pilot",
  pilotAnnouncement:
    "NomNom is live on the App Store, but deliveries are opening by invite while rider operations are prepared.",
  launchNightOrderCap: 12,
  minimumOnlineRiders: 3,
  maxSearchingOrders: 4,
  serviceHours: {
    start: "10:00",
    end: "22:00",
  },
  maxSearchRadius: 5,
  riderTimeout: 180,
};

export function toSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\\s-]/g, "")
    .replace(/\\s+/g, "_");
}

export function normalizeNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function sanitizeMoneyArray(
  value: unknown,
  fallback: number[],
): number[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  const sanitized = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item) && item >= 0)
    .map((item) => Math.round(item * 100) / 100);

  return sanitized.length > 0 ? sanitized : fallback;
}

function sanitizeWalletTopUpBonuses(
  value: unknown,
): WalletTopUpBonusTier[] {
  if (!Array.isArray(value)) {
    return DEFAULT_CONFIG.walletTopUpBonuses;
  }

  const sanitized = value
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }

      const candidate = item as {
        topUpAmount?: unknown;
        bonusAmount?: unknown;
      };
      const topUpAmount = Number(candidate.topUpAmount);
      const bonusAmount = Number(candidate.bonusAmount);

      if (
        !Number.isFinite(topUpAmount) ||
        topUpAmount < 0 ||
        !Number.isFinite(bonusAmount) ||
        bonusAmount < 0
      ) {
        return null;
      }

      return {
        topUpAmount: Math.round(topUpAmount * 100) / 100,
        bonusAmount: Math.round(bonusAmount * 100) / 100,
      };
    })
    .filter((item): item is WalletTopUpBonusTier => item !== null);

  return sanitized.length > 0 ? sanitized : DEFAULT_CONFIG.walletTopUpBonuses;
}

function pricingFieldsChanged(
  nextConfig: ConfigFormState,
  previousConfig: ConfigFormState | null,
): boolean {
  if (!previousConfig) {
    return true;
  }

  return JSON.stringify({
    deliveryFee: nextConfig.deliveryFee,
    pricingModel: nextConfig.pricingModel,
    distanceFeePerMile: nextConfig.distanceFeePerMile,
    tipPresets: nextConfig.tipPresets,
    walletTopUpPresets: nextConfig.walletTopUpPresets,
    walletTopUpBonuses: nextConfig.walletTopUpBonuses,
  }) !==
    JSON.stringify({
      deliveryFee: previousConfig.deliveryFee,
      pricingModel: previousConfig.pricingModel,
      distanceFeePerMile: previousConfig.distanceFeePerMile,
      tipPresets: previousConfig.tipPresets,
      walletTopUpPresets: previousConfig.walletTopUpPresets,
      walletTopUpBonuses: previousConfig.walletTopUpBonuses,
    });
}

export function subscribeToAppConfig(
  onUpdate: (config: ConfigFormState, createdAt: Timestamp | null) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    doc(db, "appConfig", "global"),
    (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(DEFAULT_CONFIG, null);
        return;
      }

      const data = snapshot.data() as Partial<AppConfig>;
      onUpdate(
        {
          deliveryFee:
            typeof data.deliveryFee === "number"
              ? data.deliveryFee
              : DEFAULT_CONFIG.deliveryFee,
          pricingModel:
            data.pricingModel === "distance" ? "distance" : DEFAULT_CONFIG.pricingModel,
          distanceFeePerMile:
            typeof data.distanceFeePerMile === "number"
              ? data.distanceFeePerMile
              : DEFAULT_CONFIG.distanceFeePerMile,
          tipPresets: sanitizeMoneyArray(
            data.tipPresets,
            DEFAULT_CONFIG.tipPresets,
          ),
          walletTopUpPresets: sanitizeMoneyArray(
            data.walletTopUpPresets,
            DEFAULT_CONFIG.walletTopUpPresets,
          ),
          walletTopUpBonuses: sanitizeWalletTopUpBonuses(
            data.walletTopUpBonuses,
          ),
          pricingVersion:
            typeof data.pricingVersion === "number" && data.pricingVersion >= 1
              ? Math.trunc(data.pricingVersion)
              : DEFAULT_CONFIG.pricingVersion,
          pricingEffectiveAt:
            data.pricingEffectiveAt &&
            typeof (data.pricingEffectiveAt as { toDate?: unknown }).toDate ===
              "function"
              ? (data.pricingEffectiveAt as Timestamp)
              : null,
          orderingEnabled:
            typeof data.orderingEnabled === "boolean"
              ? data.orderingEnabled
              : DEFAULT_CONFIG.orderingEnabled,
          pilotModeEnabled:
            typeof data.pilotModeEnabled === "boolean"
              ? data.pilotModeEnabled
              : DEFAULT_CONFIG.pilotModeEnabled,
          pilotCohortLabel:
            typeof data.pilotCohortLabel === "string"
              ? data.pilotCohortLabel
              : DEFAULT_CONFIG.pilotCohortLabel,
          pilotAnnouncement:
            typeof data.pilotAnnouncement === "string"
              ? data.pilotAnnouncement
              : DEFAULT_CONFIG.pilotAnnouncement,
          launchNightOrderCap:
            typeof data.launchNightOrderCap === "number"
              ? data.launchNightOrderCap
              : DEFAULT_CONFIG.launchNightOrderCap,
          minimumOnlineRiders:
            typeof data.minimumOnlineRiders === "number"
              ? data.minimumOnlineRiders
              : DEFAULT_CONFIG.minimumOnlineRiders,
          maxSearchingOrders:
            typeof data.maxSearchingOrders === "number"
              ? data.maxSearchingOrders
              : DEFAULT_CONFIG.maxSearchingOrders,
          serviceHours: {
            start: data.serviceHours?.start || DEFAULT_CONFIG.serviceHours.start,
            end: data.serviceHours?.end || DEFAULT_CONFIG.serviceHours.end,
          },
          maxSearchRadius:
            typeof data.maxSearchRadius === "number"
              ? data.maxSearchRadius
              : DEFAULT_CONFIG.maxSearchRadius,
          riderTimeout:
            typeof data.riderTimeout === "number"
              ? data.riderTimeout
              : DEFAULT_CONFIG.riderTimeout,
        },
        data.createdAt || null,
      );
    },
    (error) => {
      console.error("[ConfigService] Failed to load app config:", error);
      onError("Failed to load app configuration.");
    },
  );
}

export function subscribeToLocations(
  kind: LocationKind,
  onUpdate: (locations: LocationItem[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, kind),
    (snapshot) => {
      onUpdate(
        snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data() as { name?: string; active?: boolean };
            return {
              id: docSnap.id,
              name: data.name || docSnap.id,
              active: data.active ?? true,
            };
          })
          .sort((left, right) => left.name.localeCompare(right.name)),
      );
    },
    (error) => {
      console.error(`[ConfigService] Failed to load ${kind}:`, error);
      onError(`Failed to load ${kind === "diningHalls" ? "dining halls" : "dorms"}.`);
    },
  );
}

export async function saveAppConfig(
  config: ConfigFormState,
  previousConfig: ConfigFormState | null,
  configCreatedAt: Timestamp | null,
): Promise<void> {
  try {
    const now = Timestamp.now();
    const shouldStampPricing =
      pricingFieldsChanged(config, previousConfig) || configCreatedAt === null;
    const nextPricingVersion = shouldStampPricing
      ? Math.max(config.pricingVersion || 1, previousConfig?.pricingVersion || 1) +
        (configCreatedAt === null ? 0 : 1)
      : config.pricingVersion;
    await setDoc(
      doc(db, "appConfig", "global"),
      {
        deliveryFee: config.deliveryFee,
        pricingModel: config.pricingModel,
        distanceFeePerMile: config.distanceFeePerMile,
        tipPresets: config.tipPresets,
        walletTopUpPresets: config.walletTopUpPresets,
        walletTopUpBonuses: config.walletTopUpBonuses,
        pricingVersion: nextPricingVersion,
        pricingEffectiveAt: shouldStampPricing
          ? now
          : config.pricingEffectiveAt || null,
        orderingEnabled: config.orderingEnabled,
        pilotModeEnabled: config.pilotModeEnabled,
        pilotCohortLabel: config.pilotCohortLabel.trim(),
        pilotAnnouncement: config.pilotAnnouncement.trim(),
        launchNightOrderCap: Math.max(0, Math.trunc(config.launchNightOrderCap)),
        minimumOnlineRiders: Math.max(0, Math.trunc(config.minimumOnlineRiders)),
        maxSearchingOrders: Math.max(0, Math.trunc(config.maxSearchingOrders)),
        serviceHours: config.serviceHours,
        maxSearchRadius: config.maxSearchRadius,
        riderTimeout: config.riderTimeout,
        createdAt: configCreatedAt || now,
        updatedAt: now,
      },
      { merge: true },
    );
  } catch (error) {
    console.error("[ConfigService] Failed to save config:", error);
    throw new Error("Failed to save configuration.");
  }
}

export async function saveLocation(
  kind: LocationKind,
  mode: "add" | "edit",
  itemId: string | null,
  name: string,
  active: boolean,
): Promise<void> {
  const now = Timestamp.now();

  try {
    if (mode === "edit" && itemId) {
      await updateDoc(doc(db, kind, itemId), {
        name,
        active,
        updatedAt: now,
      });
      return;
    }

    const generatedId = toSlug(name);
    if (!generatedId) {
      throw new Error("Unable to generate an ID for this location.");
    }

    if (kind === "diningHalls") {
      await setDoc(doc(db, "diningHalls", generatedId), {
        name,
        active,
        address: {
          street: "",
          city: "San Diego",
          state: "CA",
          zipCode: "",
        },
        geofence: [],
        operatingHours: {},
        createdAt: now,
        updatedAt: now,
      });
      return;
    }

    await setDoc(doc(db, "dorms", generatedId), {
      name,
      active,
      college: "Unknown",
      address: {
        street: "",
        city: "San Diego",
        state: "CA",
        zipCode: "",
      },
      geofence: [],
      buildingCount: 0,
      roomCount: 0,
      createdAt: now,
      updatedAt: now,
    });
  } catch (error) {
    if (error instanceof Error && error.message === "Unable to generate an ID for this location.") {
      throw error;
    }
    console.error("[ConfigService] Failed to save location:", error);
    throw new Error("Failed to save location.");
  }
}

export async function deleteLocation(
  kind: LocationKind,
  itemId: string,
): Promise<void> {
  try {
    await deleteDoc(doc(db, kind, itemId));
  } catch (error) {
    console.error("[ConfigService] Failed to delete location:", error);
    throw new Error("Failed to delete location.");
  }
}
